import { useVideoScoreDisplay, setVideoScoreDisplay } from '../services/videoScoreDisplay';
import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ListVideo, RefreshCw, Youtube } from 'lucide-react';
import type { MindMapNode, YouTubeVideoEntry } from '../types';
import { getVideosForNode } from '../services/youtubeService';

// "Bản đồ" tab of YouTube Manager: every note of the notebook as one small square (habit-tracker
// style), grouped by chapter in reading order, so the whole notebook reads at a glance — which
// notes are worth a video (fill colour + number = Video-Worthy Score) and which already have one
// (tick in the corner). Hovering a square opens a floating card with the score, the AI's verdict, the
// note's screenshot and its text; clicking jumps to the note in the Notes tab.

type MapNode = MindMapNode & { _branchId?: string };

interface Props {
    nodes: MapNode[];
    branches: { id: string; title: string }[];
    notebooks: { id: string; title: string }[];
    activeNotebookId: string;
    onSelectNotebook: (id: string) => void;
    onReload: () => void;
    loading: boolean;
    vn: boolean;
    onOpenNote: (node: MapNode) => void;
    /** Chapters sorted by name (A-Z, numeric-aware) instead of canvas order — same switch as the Notes tab. */
    sortAlpha: boolean;
    onToggleSort: () => void;
    /** Open the combine panel with these videos, in map (reading) order. */
    onCombine?: (picked: { node: MapNode; video: YouTubeVideoEntry }[]) => void;
}

type Filter = 'all' | 'no-video' | 'high-no-video' | 'unscored';
const HIGH = 80;

/** Fill + text colour for a score bucket. Unscored stays neutral grey so scored notes stand out. */
export function scoreFill(score?: number): string {
    if (score == null) return 'bg-gray-200 dark:bg-slate-700 text-transparent';
    if (score >= 90) return 'bg-green-700 text-white';
    if (score >= 75) return 'bg-green-500 text-white';
    if (score >= 60) return 'bg-lime-300 dark:bg-lime-500/70 text-lime-950';
    if (score >= 45) return 'bg-amber-200 dark:bg-amber-500/60 text-amber-950';
    return 'bg-rose-200 dark:bg-rose-500/50 text-rose-950';
}

const LEGEND: { label: string; cls: string }[] = [
    { label: '90+', cls: 'bg-green-700' },
    { label: '75–89', cls: 'bg-green-500' },
    { label: '60–74', cls: 'bg-lime-300 dark:bg-lime-500/70' },
    { label: '45–59', cls: 'bg-amber-200 dark:bg-amber-500/60' },
    { label: '<45', cls: 'bg-rose-200 dark:bg-rose-500/50' },
];

const CARD_W = 380;
const LABEL_DEFAULT = 208, LABEL_MIN = 110, LABEL_MAX = 520;
const DIVIDER_GAP = 20; // space between the chapter names and the squares; the resize handle sits in it

/** Videos of a note that can be merged: only ones with a stored backup file (YouTube can't be downloaded back). */
const combinable = (n: MapNode) => getVideosForNode(n).filter(v => !!v.videoStoragePath);

export function NoteMapGrid({ nodes, branches, notebooks, activeNotebookId, onSelectNotebook, onReload, loading, vn, onOpenNote, sortAlpha, onToggleSort, onCombine }: Props) {
    const scoreDisplay = useVideoScoreDisplay();
    // Combine mode: pick squares (or a whole chapter) whose videos get merged into one new video.
    const [picking, setPicking] = useState(false);
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const togglePick = (ids: string[], on?: boolean) => setPicked(prev => {
        const next = new Set(prev);
        const turnOn = on ?? !ids.every(id => next.has(id));
        ids.forEach(id => (turnOn ? next.add(id) : next.delete(id)));
        return next;
    });
    const stopPicking = () => { setPicking(false); setPicked(new Set()); };
    const [filter, setFilter] = useState<Filter>('all');
    const [hover, setHover] = useState<{ node: MapNode; rect: DOMRect } | null>(null);

    // Chapter-name column: drag the divider to resize, double-click it to fit the longest title —
    // same behaviour as the column dividers on the Notes tab.
    const [labelW, setLabelW] = useState(LABEL_DEFAULT);
    const clampLabel = (w: number) => Math.max(LABEL_MIN, Math.min(LABEL_MAX, w));
    const startResize = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX, startW = labelW;
        const onMove = (ev: MouseEvent) => setLabelW(clampLabel(startW + ev.clientX - startX));
        const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    const autoFit = () => {
        const ctx = document.createElement('canvas').getContext('2d');
        if (!ctx) return;
        ctx.font = '600 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        const widest = groups.reduce((m, g) => Math.max(m, ctx.measureText(g.title).width), 0);
        setLabelW(clampLabel(Math.ceil(widest) + 16));
    };
    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hasVideo = (n: MapNode) => getVideosForNode(n).length > 0;

    const groups = useMemo(() => {
        const byBranch = new Map<string, MapNode[]>();
        for (const n of nodes) {
            if (!n._branchId) continue;
            const list = byBranch.get(n._branchId);
            if (list) list.push(n); else byBranch.set(n._branchId, [n]);
        }
        return branches
            .map(b => ({ id: b.id, title: b.title, notes: byBranch.get(b.id) ?? [] }))
            .filter(g => g.notes.length > 0);
    }, [nodes, branches]);

    const stats = useMemo(() => {
        const all = groups.flatMap(g => g.notes);
        const scored = all.filter(n => n.videoWorthinessScore != null);
        return {
            total: all.length,
            withVideo: all.filter(hasVideo).length,
            highNoVideo: all.filter(n => (n.videoWorthinessScore ?? -1) >= HIGH && !hasVideo(n)).length,
            unscored: all.length - scored.length,
        };
    }, [groups]);

    // Picked videos in map order (chapter by chapter, note by note) — the natural order to merge in.
    const pickedVideos = useMemo(
        () => groups.flatMap(g => g.notes).filter(n => picked.has(n.id)).flatMap(n => combinable(n).map(video => ({ node: n, video }))),
        [groups, picked],
    );

    const matches = (n: MapNode) =>
        filter === 'all' ? true
        : filter === 'no-video' ? !hasVideo(n)
        : filter === 'high-no-video' ? (n.videoWorthinessScore ?? -1) >= HIGH && !hasVideo(n)
        : n.videoWorthinessScore == null;

    const show = (node: MapNode, el: HTMLElement) => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        setHover({ node, rect: el.getBoundingClientRect() });
    };
    // Small delay so moving between neighbouring squares doesn't flicker the card.
    const hide = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setHover(null), 80);
    };

    const filters: { key: Filter; label: string; count: number }[] = [
        { key: 'all', label: vn ? 'Tất cả' : 'All', count: stats.total },
        { key: 'no-video', label: vn ? 'Chưa quay' : 'No video', count: stats.total - stats.withVideo },
        { key: 'high-no-video', label: vn ? `Điểm ≥ ${HIGH}, chưa quay` : `Score ≥ ${HIGH}, no video`, count: stats.highNoVideo },
        { key: 'unscored', label: vn ? 'Chưa chấm' : 'Unscored', count: stats.unscored },
    ];

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar: notebook, filters, legend */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                <div className="flex items-center gap-1.5">
                    <select
                        value={activeNotebookId}
                        onChange={e => onSelectNotebook(e.target.value)}
                        className="text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300 max-w-[260px]"
                    >
                        {notebooks.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                    </select>
                    <button
                        onClick={onReload}
                        disabled={loading}
                        title={vn ? 'Tải lại' : 'Reload'}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg disabled:opacity-40"
                    >
                        <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={onToggleSort}
                        title={vn
                            ? 'Mặc định: theo thứ tự sắp xếp trong canvas. Bấm để chuyển sang sắp theo tên (A-Z, hiểu số — 1.2 trước 1.10).'
                            : 'Default: canvas arrangement order. Click to sort by name instead (A-Z, numeric-aware — 1.2 before 1.10).'}
                        className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded ${sortAlpha
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    >
                        A-Z
                    </button>
                    {onCombine && (
                        <button
                            onClick={() => (picking ? stopPicking() : setPicking(true))}
                            title={vn ? 'Chọn các ô có video để ghép thành một video mới' : 'Pick squares with a video to merge into one new video'}
                            className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors ${picking
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40'}`}
                        >
                            <ListVideo size={12} /> {picking ? (vn ? 'Thoát chọn' : 'Stop picking') : (vn ? 'Ghép video' : 'Combine videos')}
                        </button>
                    )}
                </div>

                <div className="flex gap-1">
                    {filters.map(f => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${filter === f.key
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800'}`}
                        >
                            {f.label} <span className="opacity-60 tabular-nums">{f.count}</span>
                        </button>
                    ))}
                </div>

                {/* Show this score outside the Manager too */}
                <div className="flex items-center gap-3 ml-auto text-[11px] text-gray-600 dark:text-gray-300">
                    {([['canvas', vn ? 'Hiện điểm trên canvas' : 'Show scores on the canvas', vn ? 'Thêm nút "88" ở góc dưới trái canvas để bật lớp điểm tiềm năng video trên từng note' : 'Adds the "88" button at the bottom left of the canvas, which overlays each note with its video-potential score'],
                       ['note', vn ? 'Hiện điểm trong note' : 'Show the score inside a note', vn ? 'Hiện điểm tiềm năng video ở đầu màn hình note mở rộng' : 'Shows the video-potential score in the header of an opened note']] as const).map(([key, label, tip]) => (
                        <label key={key} title={tip} className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input type="checkbox" className="accent-green-600" checked={scoreDisplay[key]} onChange={e => setVideoScoreDisplay({ [key]: e.target.checked })} />
                            {label}
                        </label>
                    ))}
                </div>

                <div className="flex items-center gap-3 text-[10px] text-gray-500 dark:text-gray-400">
                    {LEGEND.map(l => (
                        <span key={l.label} className="flex items-center gap-1">
                            <span className={`w-3 h-3 rounded-[3px] ${l.cls}`} />{l.label}
                        </span>
                    ))}
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-[3px] bg-gray-200 dark:bg-slate-700" />{vn ? 'Chưa chấm' : 'Unscored'}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="relative w-3 h-3 rounded-[3px] bg-gray-200 dark:bg-slate-700"><VideoTick /></span>{vn ? 'Đã có video' : 'Has video'}
                    </span>
                </div>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
                {loading && groups.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center mt-16">{vn ? 'Đang tải…' : 'Loading…'}</p>
                ) : groups.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center mt-16">{vn ? 'Notebook này chưa có note.' : 'This notebook has no notes yet.'}</p>
                ) : (
                    <div className="relative space-y-3">
                        <div
                            onMouseDown={startResize}
                            onDoubleClick={autoFit}
                            title={vn ? 'Kéo để đổi độ rộng · nhấp đúp để vừa tên dài nhất' : 'Drag to resize · double-click to fit the longest title'}
                            className="absolute top-0 bottom-0 w-1.5 -ml-[3px] cursor-col-resize rounded bg-gray-100 dark:bg-slate-800 hover:bg-red-400 active:bg-red-500 transition-colors z-10"
                            style={{ left: labelW + DIVIDER_GAP / 2 }}
                        />
                        {groups.map(g => {
                            const done = g.notes.filter(hasVideo).length;
                            const chapterIds = g.notes.filter(n => combinable(n).length > 0).map(n => n.id);
                            const chapterAllPicked = chapterIds.length > 0 && chapterIds.every(id => picked.has(id));
                            return (
                                <div key={g.id} className="flex" style={{ gap: DIVIDER_GAP }}>
                                    <div className="flex-shrink-0 pt-1" style={{ width: labelW }}>
                                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate" title={g.title}>{g.title}</p>
                                        <p className="text-[10px] text-gray-400 tabular-nums">
                                            {done}/{g.notes.length} {vn ? 'đã quay' : 'recorded'}
                                            {picking && chapterIds.length > 0 && (
                                                <button
                                                    onClick={() => togglePick(chapterIds, !chapterAllPicked)}
                                                    className="ml-2 font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                                >
                                                    {chapterAllPicked ? (vn ? 'Bỏ chọn chương' : 'Unpick chapter') : (vn ? `Chọn cả chương (${chapterIds.length})` : `Pick chapter (${chapterIds.length})`)}
                                                </button>
                                            )}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                                        {g.notes.map(n => {
                                            const score = n.videoWorthinessScore;
                                            const canPick = combinable(n).length > 0;
                                            const isPicked = picked.has(n.id);
                                            // While picking, squares that can't be merged fade so the candidates stand out.
                                            const dim = picking ? !canPick : !matches(n);
                                            return (
                                                <button
                                                    key={n.id}
                                                    onMouseEnter={e => show(n, e.currentTarget)}
                                                    onMouseLeave={hide}
                                                    onFocus={e => show(n, e.currentTarget)}
                                                    onBlur={hide}
                                                    onClick={() => (picking ? (canPick && togglePick([n.id])) : onOpenNote(n))}
                                                    aria-label={`${n.title || 'Note'}${score != null ? ` — ${score}` : ''}`}
                                                    className={`relative w-7 h-7 rounded-[5px] text-[10px] font-bold tabular-nums flex items-center justify-center transition-transform hover:scale-125 hover:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${scoreFill(score)} ${dim ? 'opacity-15' : ''} ${picking && !canPick ? 'cursor-not-allowed' : ''} ${isPicked ? 'ring-2 ring-blue-600 ring-offset-1 ring-offset-white dark:ring-offset-slate-900' : ''}`}
                                                >
                                                    {score != null ? Math.round(score) : ''}
                                                    {hasVideo(n) && <VideoTick />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {picking && (
                <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 dark:border-slate-800 bg-blue-50/60 dark:bg-blue-900/10 flex-shrink-0">
                    <p className="text-xs text-gray-600 dark:text-gray-300 flex-1">
                        {pickedVideos.length === 0
                            ? (vn ? 'Bấm vào các ô có dấu tick để chọn video cần ghép. Ô mờ là note chưa có video, hoặc video không còn bản sao lưu.' : 'Click ticked squares to pick videos. Faded squares have no video, or a video with no stored backup.')
                            : (vn ? `Đã chọn ${pickedVideos.length} video từ ${picked.size} note — ghép theo thứ tự trên bản đồ, đổi được ở bước sau.` : `${pickedVideos.length} videos from ${picked.size} notes — merged in map order; you can reorder next.`)}
                    </p>
                    {picked.size > 0 && (
                        <button onClick={() => setPicked(new Set())} className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                            {vn ? 'Bỏ chọn' : 'Clear'}
                        </button>
                    )}
                    <button
                        onClick={() => onCombine?.(pickedVideos)}
                        disabled={pickedVideos.length < 2}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-colors"
                    >
                        <ListVideo size={13} /> {vn ? `Ghép ${pickedVideos.length || ''} video` : `Combine ${pickedVideos.length || ''} videos`}
                    </button>
                </div>
            )}

            {hover && createPortal(<HoverCard node={hover.node} rect={hover.rect} vn={vn} videoCount={getVideosForNode(hover.node).length} noBackup={hasVideo(hover.node) && combinable(hover.node).length === 0} />, document.body)}
        </div>
    );
}

/** Corner tick marking a note that already has a video (the parent must be `relative`). */
function VideoTick() {
    return (
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-600 text-white flex items-center justify-center ring-1 ring-white dark:ring-slate-900">
            <Check size={8} strokeWidth={4} />
        </span>
    );
}

function HoverCard({ node, rect, vn, videoCount, noBackup }: { node: MapNode; rect: DOMRect; vn: boolean; videoCount: number; noBackup: boolean }) {
    // Beside the square, flipped to whichever side has room; clamped so it never leaves the screen.
    const gap = 10;
    const left = rect.right + gap + CARD_W <= window.innerWidth ? rect.right + gap : Math.max(8, rect.left - gap - CARD_W);
    const maxH = Math.min(520, window.innerHeight - 16);
    const top = Math.max(8, Math.min(rect.top - 20, window.innerHeight - maxH - 8));
    const score = node.videoWorthinessScore;
    const img = node.imageBase64
        ? (node.imageBase64.startsWith('http') ? node.imageBase64 : `data:${node.imageMimeType || 'image/jpeg'};base64,${node.imageBase64}`)
        : null;
    const text = (node.content || '').trim();

    return (
        <div
            className="fixed z-[1000] pointer-events-none rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden flex flex-col"
            style={{ left, top, width: CARD_W, maxHeight: maxH }}
        >
            {/* Score + AI verdict first — that is what the map is for */}
            <div className="flex gap-3 p-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/60">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-base font-bold tabular-nums flex-shrink-0 ${score != null ? scoreFill(score) : 'bg-gray-200 dark:bg-slate-700 text-gray-400'}`}>
                    {score != null ? Math.round(score) : '–'}
                </div>
                <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Video-Worthy</p>
                    <p className="text-xs text-gray-700 dark:text-gray-200 leading-snug">
                        {score != null
                            ? (node.videoWorthinessSummary || (vn ? 'Chưa có nhận xét.' : 'No verdict saved.'))
                            : (vn ? 'Note này chưa được chấm điểm.' : 'This note has not been scored yet.')}
                    </p>
                </div>
            </div>

            <div className="p-3 overflow-hidden">
                <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">{node.title || (vn ? '(Chưa đặt tên)' : '(Untitled)')}</p>
                    {videoCount > 0 && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5">
                            <Youtube size={12} />{videoCount}
                        </span>
                    )}
                </div>
                {noBackup && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-2">
                        {vn ? 'Video này không còn bản sao lưu nên không ghép được.' : 'This video has no stored backup, so it can\'t be merged.'}
                    </p>
                )}
                {img && <img src={img} alt="" className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-slate-800 mb-2" />}
                {text && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed line-clamp-[9]">
                        {text.length > 700 ? `${text.slice(0, 700)}…` : text}
                    </p>
                )}
            </div>
        </div>
    );
}
